import {
  AsyncPropSelector,
  ALL_MODULES,
  AsyncCheckboxNew,
  AsyncRadioNew,
  AsyncSelectNew,
  CustomDateField,
  CustomTextField,
  CUSTOM_FIELDS,
  SecureText,
  getTranslatedText
} from '@nile/components';
import {
  DropdownIndicator,
  formatGroupLabel,
  IndicatorSeparator
} from '@nile/components/src/Pebbles/select';
import {
  Checkbox,
  RadioButton,
  TextField,
  Heading,
  Badge,
  DisplayText,
  Tippy
} from '@superops/ui-design-system';
import React from 'react';
import RGL, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import Select from 'react-select';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { getCellValue } from '@nile/reports/src/modules/EfficiencyReport/util';
import { useIntl } from 'react-intl';

const ReactGridLayout = WidthProvider(RGL);

function CustomField(props) {
  const {
    field,
    customFieldData,
    builderActions,
    disableSelection,
    isMandatory,
    error,
    data,
    systray = false,
    dependantFieldEnabled = false
  } = props;
  const { fieldProps } = field;
  const fieldData = customFieldData.find((el) => el.id === fieldProps.id);
  const value = fieldProps?.selectedValue || '';
  let fieldValue = fieldProps?.selectedValue || '';
  if (fieldValue && fieldData.fieldType === 'SELECT') {
    fieldValue = {
      name: fieldValue,
      value: fieldValue,
      label: fieldValue
    };
  } else if (fieldValue && fieldData.fieldType === 'MULTI_SELECT') {
    fieldValue = fieldValue.map((el) => {
      return {
        name: el,
        value: el,
        label: el
      };
    });
  }
  const updateSelection = (val) => {
    let updatedValue = '';
    if (val?.label) {
      updatedValue = val.label;
    } else if (fieldData.fieldType === 'MULTI_SELECT') {
      updatedValue = val ? val.map((el) => el?.label || el) : null;
    } else {
      updatedValue = val || null;
    }
    builderActions.updateFormField(fieldProps.name, updatedValue);
  };
  switch (fieldData.fieldType) {
    case 'RADIO':
      return (
        <AsyncRadioNew
          module={ALL_MODULES.ticket}
          defaultValue={fieldValue}
          field={field.fieldProps}
          value={fieldValue}
          updateSelection={updateSelection}
          disableSelection={disableSelection}
          isMandatory={isMandatory}
          error={error}
          systray={systray}
          dependantFieldEnabled={dependantFieldEnabled}
          data={data}
          fullWidth
        />
      );
    case 'CHECKBOX':
      return (
        <AsyncCheckboxNew
          module={ALL_MODULES.ticket}
          defaultValue={value}
          field={field.fieldProps}
          value={value}
          updateSelection={updateSelection}
          disableSelection={disableSelection}
          isMandatory={isMandatory}
          error={error}
          systray={systray}
          fullWidth
        />
      );
    case 'TEXT':
    case 'PARAGRAPH':
    case 'NUMERIC':
    case 'DECIMAL':
      return (
        <CustomTextField
          module={ALL_MODULES.ticket}
          defaultValue={value}
          field={{ ...field.fieldProps, fieldType: fieldData.fieldType }}
          value={value}
          updateSelection={updateSelection}
          disableSelection={disableSelection}
          isMandatory={isMandatory}
          error={error}
          fullWidth
        />
      );
    case 'DATE':
    case 'DATETIME':
      return (
        <CustomDateField
          module={ALL_MODULES.ticket}
          defaultValue={value}
          field={{ ...field.fieldProps, fieldType: fieldData.fieldType }}
          value={value}
          updateSelection={updateSelection}
          disableSelection={disableSelection}
          isMandatory={isMandatory}
          error={error}
          fullWidth
        />
      );
    case 'SELECT':
    case 'MULTI_SELECT':
      return (
        <AsyncSelectNew
          module={ALL_MODULES.ticket}
          defaultValue={fieldValue}
          field={{ ...field.fieldProps, fieldType: fieldData.fieldType }}
          isMulti={fieldData.fieldType === 'MULTI_SELECT'}
          value={fieldValue}
          updateSelection={updateSelection}
          disableSelection={disableSelection}
          isMandatory={isMandatory}
          error={error}
          systray={systray}
          dependantFieldEnabled={dependantFieldEnabled}
          data={data}
          fullWidth
        />
      );
    case CUSTOM_FIELDS.secureText:
      return (
        <SecureText
          module={ALL_MODULES.ticket}
          defaultValue={fieldValue}
          field={{ ...field.fieldProps, fieldType: fieldData.fieldType }}
          value={fieldValue}
          updateSelection={updateSelection}
          disableSelection={disableSelection}
          isMandatory={isMandatory}
          error={error}
          systray={systray}
          fullWidth
          // entityId={client.accountId}
        />
      );
    default:
      return '';
  }
}

function RelativeField(props) {
  const { field } = props;
  switch (field.fieldProps.value) {
    case 'CHECKBOX':
      return <Checkbox checked={false} label="Edit to add choices" />;
    case 'RADIO':
      return <RadioButton checked={false} label="Edit to add choices" />;
    case 'SELECT':
    case 'MULTI_SELECT':
      return (
        <Select
          className="rule-fields"
          defaultValue={''}
          onChange={() => {}}
          classNamePrefix="value_selector"
          formatGroupLabel={formatGroupLabel}
          components={{ IndicatorSeparator, DropdownIndicator }}
          options={[]}
          menuPortalTarget={document.body}
          menuPlacement="auto"
          closeMenuOnScroll={(e) => {
            return (
              e.target &&
              e.target.classList &&
              !(e.target.classList.value.indexOf('menu-list') > -1)
            );
          }}
          name="valueselector"
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 })
          }}
          isMulti={field.fieldProps.value === 'MULTI_SELECT'}
        />
      );
    default:
      return (
        <TextField
          placeholder="Edit text here"
          value={''}
          onChange={() => {}}
        />
      );
  }
}

const getDefaultValue = (fieldValue, fieldName) => {
  switch (fieldName) {
    case 'techGroup':
      return fieldValue
        ? {
            ...fieldValue,
            name: fieldValue.name,
            label: fieldValue.name,
            value: fieldValue.name
          }
        : '';
    case 'technician':
      return fieldValue
        ? {
            ...fieldValue,
            name: `${fieldValue.firstName} ${fieldValue.lastName}`,
            label: `${fieldValue.firstName} ${fieldValue.lastName}`,
            value: `${fieldValue.firstName} ${fieldValue.lastName}`
          }
        : '';
    default:
      return fieldValue
        ? { name: fieldValue, label: fieldValue, value: fieldValue }
        : '';
  }
};

const getParentFieldValue = (layout, currentData) => {
  const nestedTo =
    currentData?.nestedTo || currentData?.parentField?.columnName;
  let parentField = null;
  let parentFieldValue = null;
  if (nestedTo) {
    parentField = layout.find((el) => el?.field?.fieldProps?.name === nestedTo);
    parentFieldValue = parentField?.field?.fieldProps?.selectedValue;
  }
  return parentFieldValue;
};

function RenderItem(props) {
  const {
    item,
    layout,
    customFieldData,
    defaultData,
    builderActions,
    disableSelection,
    mandatoryFieldsOnCreation,
    errors,
    systray = false,
    dependantFieldEnabled = false,
    data
  } = props;

  const updateSelection = (f, val) => {
    const nestedAttribute = f?.nestedAttribute || null;
    const dependantField =
      nestedAttribute &&
      layout.find((el) => el?.field?.fieldProps?.name === nestedAttribute);
    const dependantName = dependantField?.field?.fieldProps?.name || null;
    builderActions.updateFormField(f.name, val, dependantName);
  };

  const currentFieldName = item?.field?.fieldProps?.name;

  const getError = (fieldType) => {
    switch (fieldType) {
      case 'custom':
        const key = `customFields.${currentFieldName}`;
        return errors?.[key]?.[0];
      default:
        return errors?.[currentFieldName]?.[0];
    }
  };

  const error = getError(item?.field?.type) || null;

  const isMandatory =
    mandatoryFieldsOnCreation &&
    mandatoryFieldsOnCreation.some((el) => el.columnName === currentFieldName);

  switch (item.field.type) {
    case 'custom':
      let currentData = customFieldData.find(
        (el) => el.id === item.field.fieldProps.id
      );
      return currentData ? (
        <div className="flex fcol">
          {/* <Title>{currentData.label}</Title> */}
          <CustomField
            builderActions={builderActions}
            field={item.field}
            customFieldData={customFieldData}
            disableSelection={disableSelection}
            isMandatory={isMandatory}
            error={error}
            systray={systray}
            dependantFieldEnabled={dependantFieldEnabled}
            data={data}
          />
        </div>
      ) : (
        ''
      );
    case 'basic':
      return (
        <div className="flex fcol">
          <Heading element="h3">Name of the field</Heading>
          <RelativeField
            field={item.field}
            disableSelection={disableSelection}
          />
        </div>
      );
    case 'section':
      return (
        <Heading element="h3" weight="Bold">
          {item?.field?.fieldProps?.label || 'Untitled section'}
        </Heading>
      );
    case 'default':
      const fieldToCompare =
        item.field.fieldProps.name || item.field.fieldProps?.id;
      let _currentData = defaultData.find((el) => el.name === fieldToCompare);

      if (_currentData && _currentData.name) {
        const defaultValue = getDefaultValue(
          item.field?.fieldProps?.selectedValue,
          _currentData?.name
        );
        const parentFieldValue = getParentFieldValue(layout, _currentData);
        return (
          <Field>
            <AsyncPropSelector
              defaultValue={defaultValue}
              updateSelection={updateSelection}
              field={_currentData}
              isDisabled={disableSelection}
              isMandatory={isMandatory}
              error={error}
              parentValue={parentFieldValue}
              systray={systray}
              dependantFieldEnabled={dependantFieldEnabled}
            />
          </Field>
        );
      }
      return <></>;

    case 'sectionEnd':
      return <SectionEnd />;
    case 'condition':
      return '';
    default:
      return <div>{item.field.name || item.field.label}</div>;
  }
}

function FormTemplate(props) {
  const {
    disableSelection,
    contentHeight,
    mandatoryFieldsOnCreation,
    errors,
    systray = false,
    dependantFieldEnabled = false
  } = props;
  const intl = useIntl();
  const { context } = props;
  const { ticket } = context || {};
  const {
    customFieldData,
    builderActions,
    defaultData,
    passedConditions
  } = props;
  const layout =
    context?.value?.fieldDetails?.layout?.map((el) => {
      return {
        ...el,
        isDraggable: false,
        isResizable: false
      };
    }) || [];

  const nonConditionItems = layout.filter((el) => {
    if (el.field.type !== 'condition' && !el.field.conditions) {
      return true;
    }
    if (el.field.type !== 'condition' && el.field.conditions) {
      const conditionId = el.field.conditions.matchCondition;
      return passedConditions.some((ele) => ele.i === conditionId);
    }
  });

  return (
    <Layout className="custom-form-layout" contentHeight={contentHeight}>
      <ReactGridLayout
        className="layout"
        layout={layout}
        cols={2}
        rowHeight={40}
        margin={[10, 10]}
        preventCollision={false}
        verticalCompact
      >
        {nonConditionItems.map((item) => {
          const parentField = item?.field?.fieldProps?.parentField;
          const colName = item?.field?.fieldProps?.parentField?.columnName;
          const isCustom = parentField?.fieldCategory === 'CUSTOM';
          const parentKey = isCustom ? `customFields.${colName}` : colName;
          const parentValue = getCellValue(
            { ...ticket, ...ticket?.customFields },
            systray ? colName : parentKey
          );
          const prntValue =
            typeof parentValue === 'object'
              ? parentValue?.name || parentValue?.id
              : parentValue;
          const parentSelected = parentField ? prntValue : true;
          return (
            <ElementWrap
              key={item.i}
              gitem={item}
              css={{ border: '1px solid #dfdfdf' }}
              {...item}
            >
              {parentField && !parentSelected ? (
                <Tippy
                  title={`${getTranslatedText(
                    intl,
                    'common.ticket.field.dependant.title',
                    {
                      name: parentField?.label
                    }
                  )}`}
                  duration={250}
                  delay={100}
                  placement="bottom-start"
                  arrow={false}
                  theme="dark"
                  appendTo={document.body}
                >
                  <RenderItem
                    defaultData={defaultData}
                    builderActions={builderActions}
                    item={item}
                    customFieldData={customFieldData}
                    layout={layout}
                    disableSelection={!parentSelected || disableSelection}
                    mandatoryFieldsOnCreation={mandatoryFieldsOnCreation || []}
                    errors={errors}
                    systray={systray}
                    dependantFieldEnabled={dependantFieldEnabled}
                    data={ticket}
                  />
                </Tippy>
              ) : (
                <RenderItem
                  defaultData={defaultData}
                  builderActions={builderActions}
                  item={item}
                  customFieldData={customFieldData}
                  layout={layout}
                  disableSelection={!parentSelected || disableSelection}
                  mandatoryFieldsOnCreation={mandatoryFieldsOnCreation || []}
                  errors={errors}
                  systray={systray}
                  dependantFieldEnabled={dependantFieldEnabled}
                  data={ticket}
                />
              )}
            </ElementWrap>
          );
        })}
      </ReactGridLayout>
    </Layout>
  );
}

const ElementWrap = styled.div`
  overflow: auto;
  border: none !important;
  .cf-wrapper {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
  .cf-label {
    margin-left: 0 !important;
    margin-top: 0 !important;
  }
`;

const Layout = styled.div`
  z-index: 1;
  height: 100%;
  width: 60vw;
  overflow: auto;
  background: var(--sops-white-transparent-bg);
  display: flex;
  justify-content: center;
  .react-grid-layout {
    height: auto ${(props) => (props.contentHeight ? '' : '!important')};
    width: 100% !important;
    overflow: auto;
    border-radius: 8px;
    border: 1px solid #dfdfdf;
  }
  .Polaris-Connected__Item {
    flex: 0 0 auto !important;
  }
  .header-tag {
    margin-bottom: 0 !important;
  }
`;

const SectionEnd = styled.div`
  border-bottom: 1px solid #dfdfdf;
`;

const Field = styled.div`
  width: 100%;
  flex-grow: 1;
  padding-right: 10px;
`;

export default FormTemplate;
